import { useCallback, useEffect, useRef, useState } from 'react';
import {
	applyDragDelta,
	applyResizeDelta,
	clampFloatingRect,
	getViewportSize
} from './floatingPanelGeometry';

const getClientPoint = (event) => ({
	x: event.clientX ?? 0,
	y: event.clientY ?? 0
});

export const useFloatingPanel = ({
	enabled,
	bounds,
	defaultGeometry,
	lockedGeometry = null,
	interactionDisabled = false,
	onResizeStart
}) => {
	const [geometry, setGeometry] = useState(() => clampFloatingRect(defaultGeometry(getViewportSize()), {
		...bounds,
		viewport: getViewportSize()
	}));
	const [interaction, setInteraction] = useState(null);
	const geometryRef = useRef(geometry);
	geometryRef.current = geometry;

	const displayedGeometry = lockedGeometry || geometry;
	const isInteracting = Boolean(interaction);

	const resetGeometry = useCallback(() => {
		const viewport = getViewportSize();
		setGeometry(clampFloatingRect(defaultGeometry(viewport), { ...bounds, viewport }));
	}, [bounds, defaultGeometry]);

	useEffect(() => {
		if (!enabled) {
			return;
		}
		const onResize = () => {
			const viewport = getViewportSize();
			setGeometry((current) => clampFloatingRect(current, { ...bounds, viewport }));
		};
		window.addEventListener('resize', onResize);
		return () => window.removeEventListener('resize', onResize);
	}, [enabled, bounds]);

	useEffect(() => {
		if (!interaction) {
			return undefined;
		}
		const onPointerMove = (event) => {
			event.preventDefault();
			const point = getClientPoint(event);
			const viewport = getViewportSize();
			const boundConfig = { ...bounds, viewport };
			const deltaX = point.x - interaction.startClientX;
			const deltaY = point.y - interaction.startClientY;

			if (interaction.type === 'drag') {
				setGeometry(applyDragDelta(interaction.startGeometry, deltaX, deltaY, boundConfig));
				return;
			}
			setGeometry(applyResizeDelta(
				interaction.startGeometry,
				interaction.handle,
				deltaX,
				deltaY,
				boundConfig
			));
		};
		const onPointerUp = (event) => {
			event.preventDefault();
			setInteraction(null);
		};
		document.addEventListener('pointermove', onPointerMove);
		document.addEventListener('pointerup', onPointerUp);
		document.addEventListener('pointercancel', onPointerUp);
		return () => {
			document.removeEventListener('pointermove', onPointerMove);
			document.removeEventListener('pointerup', onPointerUp);
			document.removeEventListener('pointercancel', onPointerUp);
		};
	}, [interaction, bounds]);

	const startDrag = useCallback((event) => {
		if (!enabled || interactionDisabled || lockedGeometry) {
			return;
		}
		if (event.button != null && event.button !== 0) {
			return;
		}
		event.preventDefault();
		event.stopPropagation();
		const point = getClientPoint(event);
		setInteraction({
			type: 'drag',
			startClientX: point.x,
			startClientY: point.y,
			startGeometry: { ...geometryRef.current }
		});
	}, [enabled, interactionDisabled, lockedGeometry]);

	const startResize = useCallback((handle, event) => {
		if (!enabled || interactionDisabled) {
			return;
		}
		if (event.button != null && event.button !== 0) {
			return;
		}
		event.preventDefault();
		event.stopPropagation();
		const point = getClientPoint(event);
		onResizeStart?.();
		setInteraction({
			type: 'resize',
			handle,
			startClientX: point.x,
			startClientY: point.y,
			startGeometry: { ...(lockedGeometry || geometryRef.current) }
		});
	}, [enabled, interactionDisabled, lockedGeometry, onResizeStart]);

	const setGeometryDirect = useCallback((nextGeometry) => {
		const viewport = getViewportSize();
		setGeometry(clampFloatingRect(nextGeometry, { ...bounds, viewport }));
	}, [bounds]);

	return {
		geometry,
		displayedGeometry,
		isInteracting,
		resetGeometry,
		setGeometry: setGeometryDirect,
		startDrag,
		startResize
	};
};
